import * as yup from "yup";
import { useInfiniteQuery } from "@tanstack/react-query";
import moment from "moment";
import { API_DATE_FORMAT, HTTP_ENDPOINTS, QUERY_KEYS } from "../../consts/api";
import { http } from "../../services/HttpService";
import { removeNullishFromShape, unwrapOr } from "../../utilities/general";
import { createQueryKeyFromShape } from "../../utilities/api";
import {
  composePaginationSchema,
  getNextPaginationPage,
  paginationSchema,
} from "../../schemas/pagination";

export const ORDER_BY = {
  name: "name",
  type: "doc_type",
  date: "created_at",
  expiry: "expiration_date",
};

export const DOCUMENT_TYPES = {
  cancelPolicy: "cancellation_policy",
  questionnaire: "questionnaire",
  providerQuestionnaire: "provider_questionnaire",
  medicalHistory: "medical_history",
  ePrescription: "ePrescription",
  agreement: "agreement",
  procedure: "procedure",
  consent: "consent",
  file: "file",
  eFax: "efax",
  gfe: "gfe",
};

export const DOC_TYPE_BY_LABEL = {
  "Cancel Policy": DOCUMENT_TYPES.cancelPolicy,
  Questionnaire: DOCUMENT_TYPES.questionnaire,
  "Provider Questionnaire": DOCUMENT_TYPES.providerQuestionnaire,
  "Medical History": DOCUMENT_TYPES.medicalHistory,
  ePrescription: DOCUMENT_TYPES.ePrescription,
  Agreement: DOCUMENT_TYPES.agreement,
  Procedure: DOCUMENT_TYPES.procedure,
  Consent: DOCUMENT_TYPES.consent,
  File: DOCUMENT_TYPES.file,
  eFax: DOCUMENT_TYPES.eFax,
  GFE: DOCUMENT_TYPES.gfe,
};

const requestSchema = yup.object({
  patientId: yup.number().required(),

  pageSize: yup.number().required(),

  searchTerm: yup.string().nullable(),

  period: yup
    .object({
      from: yup.date().nullable(),
      to: yup.date().nullable(),
    })
    .nullable(),

  filter: yup
    .object({
      type: yup.string().nullable(),
      expired: yup.boolean(),
    })
    .nullable(),

  limit: yup.number().nullable(),

  order: yup
    .object({
      by: yup.string().nullable(),
      direction: yup.string().nullable(),
    })
    .nullable(),
});

const responseSchema = paginationSchema.concat(
  yup.object({
    docTypes: yup
      .array()
      .of(
        yup.object({
          label: yup.string().required(),
          value: yup.string().required(),
        }),
      )
      .required(),
    documents: yup
      .array()
      .of(
        yup.object({
          acknowledged_at: yup.string().nullable(),
          can_edit: yup.boolean().required(),
          created_at: yup.string().required(),
          created_by: yup.string().nullable(),
          description: yup.string().nullable(),
          expiration_date: yup.string().nullable(),
          expiration_date_editor: yup.string().nullable(),
          id: yup.number().required(),
          name: yup.string().required(),
          type: yup.string().required(),
          type_label: yup.string().required(),
          url: yup.string().required(),
          is_lock: yup.boolean().required(),
          is_expired: yup.boolean().nullable(),
          expire_soon: yup.boolean().required(),
          procedure_id: yup.number().nullable(),
          object_id: yup.number().nullable(),
          is_gfe: yup.boolean().nullable(),
          gfe: yup.string().nullable(),
          last_edited_expiration_date: yup.string().nullable(),
        }),
      )
      .required(),
  }),
);

export const composeResponse = (res, req) => {
  const data = res.data?.data || {};
  const documents = data.documents || [];
  const types = data.doc_types || {};
  const pagination = data.report || data;
  const pageSize = req.pageSize;
  const lastPage = pagination.last_page;
  const currentPage = pagination.current_page;

  return {
    ...composePaginationSchema({
      response: {
        ...pagination,
        has_next_page:
          pagination.has_next_page ||
          (typeof currentPage === "number" &&
            typeof lastPage === "number" &&
            currentPage < lastPage),
      },
      pageSize,
    }),
    docTypes: Object.entries(types).map(([label, value]) => ({
      label,
      value,
    })),
    documents,
  };
};

const createKeyFromShape = (shape) =>
  createQueryKeyFromShape(removeNullishFromShape(shape));

export function usePatientDocumentsQuery({ payload = {}, options = {} } = {}) {
  const req = requestSchema.validateSync(payload, {
    strict: true,
  });

  const fromDate = unwrapOr(
    () => moment(req.period.from).format(API_DATE_FORMAT),
    null,
  );
  const toDate = unwrapOr(
    () => moment(req.period.to).format(API_DATE_FORMAT),
    null,
  );

  return useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.patientDocuments.all,
      createKeyFromShape(req.order),
      createKeyFromShape(req.filter),
      fromDate,
      toDate,
      req.limit,
      req.pageSize,
      req.patientId,
      req.searchTerm,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await http.get(HTTP_ENDPOINTS.patientDocuments.getAll(), {
        params: removeNullishFromShape({
          patient_id: req.patientId,
          term: req.searchTerm || undefined,
          type: req.filter?.type || undefined,
          expired: req.filter?.expired,
          sort_by: req.order?.by,
          order: req.order?.direction,
          pagesize: req.pageSize,
          start_date: fromDate,
          end_date: toDate,
          page: pageParam,
        }),
      });
      return responseSchema.validateSync(composeResponse(res, req), {
        strict: true,
      });
    },
    ...options,
    getNextPageParam: getNextPaginationPage,
  });
}
